// src/pages/WeeklyReportDetail.js
// Weekly AI Trend Report Detail View
// Displays Claude AI-generated analysis with "Answers First" structure

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWeeklyReport } from '../services/reportsService';
import './WeeklyReportDetail.css';

// Severity color mapping - uses CSS variables for theme awareness
const getSeverityColor = (severity) => {
  const colors = {
    'critical': 'var(--severity-critical, #ff0000)',
    'high': 'var(--severity-high, #ff6b35)',
    'medium': 'var(--severity-medium, #F3BA24)',
    'low': 'var(--severity-low, #4caf50)'
  };
  return colors[severity?.toLowerCase()] || 'var(--text-muted, #888)';
};

// Trend direction indicator - uses CSS variables
const TrendIndicator = ({ direction }) => {
  const indicators = {
    'improving': { icon: '\u2191', color: 'var(--severity-low, #39ff14)', label: 'Improving' },
    'stable': { icon: '\u2192', color: 'var(--severity-medium, #F3BA24)', label: 'Stable' },
    'degrading': { icon: '\u2193', color: 'var(--severity-critical, #ff4444)', label: 'Degrading' }
  };
  const trend = indicators[direction?.toLowerCase()] || indicators['stable'];
  return (
    <span className="trend-indicator" style={{ color: trend.color }}>
      {trend.icon} {trend.label}
    </span>
  );
};

// Effort badge component - uses CSS variables
const EffortBadge = ({ effort }) => {
  const styles = {
    'low': { bg: 'rgba(57, 255, 20, 0.15)', color: 'var(--severity-low, #39ff14)' },
    'medium': { bg: 'rgba(243, 186, 36, 0.15)', color: 'var(--severity-medium, #F3BA24)' },
    'high': { bg: 'rgba(255, 107, 53, 0.15)', color: 'var(--severity-high, #ff6b35)' }
  };
  const style = styles[effort?.toLowerCase()] || styles['medium'];
  return (
    <span className="effort-badge" style={{ backgroundColor: style.bg, color: style.color }}>
      {effort || 'Medium'} effort
    </span>
  );
};

// Urgency badge for account-shape assetPrioritization items.
const UrgencyBadge = ({ urgency }) => {
  const styles = {
    'immediate': { bg: 'rgba(255, 0, 0, 0.18)', color: 'var(--severity-critical, #ff4444)' },
    'soon': { bg: 'rgba(255, 107, 53, 0.18)', color: 'var(--severity-high, #ff6b35)' },
    'monitor': { bg: 'rgba(243, 186, 36, 0.18)', color: 'var(--severity-medium, #F3BA24)' }
  };
  const style = styles[urgency?.toLowerCase()] || styles['monitor'];
  return (
    <span className="urgency-badge" style={{
      backgroundColor: style.bg,
      color: style.color,
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      {urgency || 'monitor'}
    </span>
  );
};

// PRD-78 Block C: account-shape view (rolled-up portfolio report).
// Renders when report.reportType === 'account-weekly' | 'account-monthly'
// OR when report.aiAnalysis?.portfolioHealth is present (defensive).
function AccountReportView({ report, navigate }) {
  const { aiAnalysis = {}, summary = {}, assets = [], month, weekNumber } = report;
  const isMonthly = report.reportType === 'account-monthly' || !!month;
  const periodLabel = isMonthly
    ? new Date(`${month || '2026-01'}-15`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : weekNumber;

  const mom = aiAnalysis.monthOverMonth || aiAnalysis.weekOverWeek || {};
  const momLabel = isMonthly ? 'Month-over-Month' : 'Week-over-Week';
  const totals = summary.totalVulnerabilities || {};

  return (
    <div className="weekly-report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/reports?type=trend-weekly')}>
            &larr; Reports
          </button>
          <h1 className="report-title">
            {isMonthly ? 'Monthly Portfolio Rollup' : 'Weekly Account Summary'}
          </h1>
          <span className="report-week">{periodLabel}</span>
        </div>
        <div className="header-right">
          <span className="report-target">{summary.totalAssets || 0} assets</span>
          <span className="report-date">
            Generated: {new Date(report.aiGeneratedAt || report.generatedAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Portfolio summary bar */}
      <div className="risk-summary-bar">
        <div className="risk-item">
          <span className="risk-count critical">{totals.critical || 0}</span>
          <span className="risk-label">Critical</span>
        </div>
        <div className="risk-item">
          <span className="risk-count high">{totals.high || 0}</span>
          <span className="risk-label">High</span>
        </div>
        <div className="risk-item">
          <span className="risk-count medium">{totals.medium || 0}</span>
          <span className="risk-label">Medium</span>
        </div>
        <div className="risk-item">
          <span className="risk-count low">{totals.low || 0}</span>
          <span className="risk-label">Low</span>
        </div>
        <div className="risk-item trend">
          <TrendIndicator direction={mom.direction || summary.monthOverMonth?.riskTrend} />
          <span className="risk-label">{momLabel}</span>
        </div>
      </div>

      {/* Portfolio Health (account-shape executive summary) */}
      <section className="report-section executive-summary">
        <h2 className="section-title">Portfolio Health</h2>
        <p className="summary-text">
          {aiAnalysis.portfolioHealth || 'No portfolio health summary available.'}
        </p>
      </section>

      {/* Risk Distribution + Asset Prioritization */}
      {aiAnalysis.riskDistribution && (
        <section className="report-section">
          <h2 className="section-title">Risk Distribution</h2>
          <div className="section-content">
            {aiAnalysis.riskDistribution.summary && (
              <p className="summary-text" style={{ marginBottom: 16 }}>
                {aiAnalysis.riskDistribution.summary}
              </p>
            )}
            {aiAnalysis.riskDistribution.assetPrioritization?.length > 0 && (
              <div className="recommendations-list">
                {aiAnalysis.riskDistribution.assetPrioritization.map((item, idx) => {
                  const assetMatch = assets.find(a => a.target === item.target);
                  return (
                    <div key={idx} className="recommendation-card">
                      <div className="rec-header">
                        <UrgencyBadge urgency={item.urgency} />
                        <span className="rec-action" style={{ fontWeight: 600, fontSize: 15 }}>
                          {item.target}
                        </span>
                        {assetMatch?.reportId && (
                          <button
                            className="nav-button"
                            style={{ marginLeft: 'auto', fontSize: 11, padding: '4px 10px' }}
                            onClick={() => navigate(`/weekly-report/${assetMatch.reportId}`)}
                          >
                            View per-asset →
                          </button>
                        )}
                      </div>
                      <p className="rec-context" style={{ marginTop: 8 }}>{item.rationale}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Cross-Asset Insights */}
      {aiAnalysis.crossAssetInsights?.length > 0 && (
        <section className="report-section">
          <h2 className="section-title">
            Cross-Asset Insights
            <span className="section-count">{aiAnalysis.crossAssetInsights.length}</span>
          </h2>
          <div className="section-content">
            <div className="recommendations-list">
              {aiAnalysis.crossAssetInsights.map((insight, idx) => (
                <div key={idx} className="recommendation-card">
                  <div className="rec-header">
                    <span
                      className="rec-severity"
                      style={{
                        backgroundColor: `${getSeverityColor(insight.severity)}20`,
                        color: getSeverityColor(insight.severity),
                        border: `1px solid ${getSeverityColor(insight.severity)}`
                      }}
                    >
                      {insight.severity?.toUpperCase() || 'MEDIUM'}
                    </span>
                  </div>
                  <p className="rec-action">{insight.pattern}</p>
                  {insight.recommendation && (
                    <p className="rec-context"><strong>Recommendation:</strong> {insight.recommendation}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Month/Week over previous period */}
      <section className="report-section">
        <h2 className="section-title">{momLabel}</h2>
        <div className="section-content">
          <div className="rec-header" style={{ marginBottom: 12 }}>
            <TrendIndicator direction={mom.direction || 'stable'} />
            {summary.monthOverMonth && (
              <>
                <span style={{ color: 'var(--text-muted, #888)', fontSize: 13, marginLeft: 16 }}>
                  Vuln Δ: {(summary.monthOverMonth.vulnChange ?? 0) > 0 ? '+' : ''}
                  {summary.monthOverMonth.vulnChange ?? 0}
                </span>
                <span style={{ color: 'var(--text-muted, #888)', fontSize: 13, marginLeft: 16 }}>
                  Critical Δ: {(summary.monthOverMonth.criticalChange ?? 0) > 0 ? '+' : ''}
                  {summary.monthOverMonth.criticalChange ?? 0}
                </span>
              </>
            )}
          </div>
          {mom.keyChanges?.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {mom.keyChanges.map((change, idx) => (
                <li key={idx} style={{ marginBottom: 6 }}>{change}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Strategic Outlook */}
      {aiAnalysis.strategicOutlook && (
        <section className="report-section">
          <h2 className="section-title">Strategic Outlook</h2>
          <p className="summary-text">{aiAnalysis.strategicOutlook}</p>
        </section>
      )}

      {/* Asset roster footer */}
      {assets.length > 0 && (
        <section className="report-section">
          <h2 className="section-title">
            Assets in this Rollup
            <span className="section-count">{assets.length}</span>
          </h2>
          <div className="section-content">
            <div className="recommendations-list">
              {assets.map((a, idx) => (
                <div key={idx} className="recommendation-card">
                  <div className="rec-header">
                    <span className="rec-action" style={{ fontWeight: 600 }}>
                      {a.target}
                    </span>
                    <span
                      className="rec-severity"
                      style={{
                        backgroundColor: `${getSeverityColor(a.riskLevel)}20`,
                        color: getSeverityColor(a.riskLevel),
                        border: `1px solid ${getSeverityColor(a.riskLevel)}`
                      }}
                    >
                      {a.riskLevel}
                    </span>
                    {a.reportId && (
                      <button
                        className="nav-button"
                        style={{ marginLeft: 'auto', fontSize: 11, padding: '4px 10px' }}
                        onClick={() => navigate(`/weekly-report/${a.reportId}`)}
                      >
                        View →
                      </button>
                    )}
                  </div>
                  {a.executiveSummary && (
                    <p className="rec-context" style={{ marginTop: 8 }}>
                      {a.executiveSummary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function WeeklyReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    recommendations: true,
    pentest: false,
    compliance: false,
    tracking: false,
    drilldown: false
  });

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[WeeklyReport] Loading report:', reportId);

      const response = await getWeeklyReport(reportId);
      console.log('[WeeklyReport] Received:', response);

      if (response.success && response.report) {
        setReport(response.report);
      } else {
        throw new Error(response.error || 'Failed to load report');
      }
    } catch (err) {
      console.error('[WeeklyReport] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownloadPDF = async () => {
    if (pdfGenerating || !report) return;
    setPdfGenerating(true);
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const { default: saveAs } = await import('file-saver');
      const { default: WeeklyReportPDFComp } = await import('../components/reports/WeeklyReportPDF');
      const element = React.createElement(WeeklyReportPDFComp, { report });
      const blob = await pdf(element).toBlob();
      saveAs(blob, `driftalarm-weekly-${report.weekNumber || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="weekly-report-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">LOADING WEEKLY REPORT...</p>
          <p className="loading-subtext">Retrieving AI analysis</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weekly-report-container">
        <div className="error-state">
          <h2 className="error-title">ERROR</h2>
          <p className="error-text">{error}</p>
          <button onClick={loadReport} className="nav-button">
            Retry
          </button>
          <button onClick={() => navigate('/reports')} className="nav-button">
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="weekly-report-container">
        <div className="error-state">
          <h2 className="error-title">Report Not Found</h2>
          <button onClick={() => navigate('/reports')} className="nav-button">
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  // PRD-78 Block C: type-switch for account-shape reports. Account rollups
  // (written by /account-{weekly,monthly}-report-generate) lack the per-asset
  // executiveSummary/actionableRecommendations the rest of this component
  // assumes. Render a dedicated view instead.
  const isAccountShape = report.reportType === 'account-weekly'
    || report.reportType === 'account-monthly'
    || !!report.aiAnalysis?.portfolioHealth;
  if (isAccountShape) {
    return <AccountReportView report={report} navigate={navigate} />;
  }

  const { aiAnalysis, inputSummary, weekNumber, generatedAt } = report;

  return (
    <div className="weekly-report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/reports')}>
            &larr; Reports
          </button>
          <h1 className="report-title">Weekly Trend Report</h1>
          <span className="report-week">{weekNumber}</span>
        </div>
        <div className="header-right">
          <span className="report-target">{inputSummary?.target || 'Unknown Target'}</span>
          <span className="report-date">
            Generated: {new Date(generatedAt).toLocaleDateString()}
          </span>
          <button
            className="nav-button"
            onClick={handleDownloadPDF}
            disabled={pdfGenerating}
            style={{ marginTop: '8px' }}
          >
            {pdfGenerating ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Risk Summary Bar */}
      <div className="risk-summary-bar">
        <div className="risk-item">
          <span className="risk-level" style={{ color: getSeverityColor(inputSummary?.riskLevel) }}>
            {inputSummary?.riskLevel || 'Unknown'}
          </span>
          <span className="risk-label">Risk Level</span>
        </div>
        <div className="risk-item">
          <span className="risk-count critical">{inputSummary?.totalVulnerabilities?.critical || 0}</span>
          <span className="risk-label">Critical</span>
        </div>
        <div className="risk-item">
          <span className="risk-count high">{inputSummary?.totalVulnerabilities?.high || 0}</span>
          <span className="risk-label">High</span>
        </div>
        <div className="risk-item">
          <span className="risk-count medium">{inputSummary?.totalVulnerabilities?.medium || 0}</span>
          <span className="risk-label">Medium</span>
        </div>
        <div className="risk-item trend">
          {aiAnalysis?.trendAnalysis && (
            <TrendIndicator direction={aiAnalysis.trendAnalysis.direction} />
          )}
          <span className="risk-label">Trend</span>
        </div>
      </div>

      {/* Executive Summary - Always Visible */}
      <section className="report-section executive-summary">
        <h2 className="section-title">Executive Summary</h2>
        <p className="summary-text">
          {aiAnalysis?.executiveSummary || 'No executive summary available.'}
        </p>
      </section>

      {/* Technology Stack Section */}
      {aiAnalysis?.technologyStack && (
        <section className="report-section tech-stack-section">
          <h2 className="section-title">Technology Stack</h2>
          <div className="section-content">
            {aiAnalysis.technologyStack.summary && (
              <p className="summary-text">{aiAnalysis.technologyStack.summary}</p>
            )}
            {aiAnalysis.technologyStack.keyRisks?.length > 0 && (
              <div className="tech-risks-list">
                <span className="tech-risks-label">Key Risks:</span>
                <ul>
                  {aiAnalysis.technologyStack.keyRisks.map((risk, idx) => (
                    <li key={idx}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.technologyStack.recommendation && (
              <div className="tech-recommendation">
                <strong>Recommendation:</strong> {aiAnalysis.technologyStack.recommendation}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Actionable Recommendations - ANSWERS FIRST */}
      <section className="report-section">
        <button
          className="section-header"
          onClick={() => toggleSection('recommendations')}
        >
          <h2 className="section-title">
            Actionable Recommendations
            <span className="section-count">
              {aiAnalysis?.actionableRecommendations?.length || 0}
            </span>
          </h2>
          <span className={`toggle-icon ${expandedSections.recommendations ? 'open' : ''}`}>
            &#9660;
          </span>
        </button>
        {expandedSections.recommendations && (
          <div className="section-content">
            {aiAnalysis?.actionableRecommendations?.length > 0 ? (
              <div className="recommendations-list">
                {aiAnalysis.actionableRecommendations.map((rec, idx) => (
                  <div key={idx} className="recommendation-card">
                    <div className="rec-header">
                      <span className="rec-rank">#{rec.rank || idx + 1}</span>
                      <span
                        className="rec-severity"
                        style={{
                          backgroundColor: `${getSeverityColor(rec.severity)}20`,
                          color: getSeverityColor(rec.severity),
                          border: `1px solid ${getSeverityColor(rec.severity)}`
                        }}
                      >
                        {rec.severity?.toUpperCase() || 'MEDIUM'}
                      </span>
                      <EffortBadge effort={rec.effort} />
                      {rec.deadline && (
                        <span className="rec-deadline">{rec.deadline}</span>
                      )}
                    </div>
                    <p className="rec-action">{rec.action}</p>
                    {rec.context && (
                      <p className="rec-context">{rec.context}</p>
                    )}
                    {rec.relatedFindings?.length > 0 && (
                      <div className="rec-findings">
                        Related: {rec.relatedFindings.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No recommendations available.</p>
            )}
          </div>
        )}
      </section>

      {/* Pentest Roadmap */}
      <section className="report-section">
        <button
          className="section-header"
          onClick={() => toggleSection('pentest')}
        >
          <h2 className="section-title">Pentest Roadmap</h2>
          <span className={`toggle-icon ${expandedSections.pentest ? 'open' : ''}`}>
            &#9660;
          </span>
        </button>
        {expandedSections.pentest && aiAnalysis?.pentestRoadmap && (
          <div className="section-content">
            {aiAnalysis.pentestRoadmap.prioritizationRationale && (
              <p className="rationale">{aiAnalysis.pentestRoadmap.prioritizationRationale}</p>
            )}

            {aiAnalysis.pentestRoadmap.immediateTargets?.length > 0 && (
              <div className="pentest-group">
                <h3 className="group-title">Immediate (This Week)</h3>
                <div className="pentest-targets">
                  {aiAnalysis.pentestRoadmap.immediateTargets.map((target, idx) => (
                    <div key={idx} className="pentest-target">
                      <span className="target-name">{target.target}</span>
                      <span className="target-type">{target.testType}</span>
                      <p className="target-rationale">{target.rationale}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {aiAnalysis.pentestRoadmap.shortTermTargets?.length > 0 && (
              <div className="pentest-group">
                <h3 className="group-title">Short Term (30 Days)</h3>
                <div className="pentest-targets">
                  {aiAnalysis.pentestRoadmap.shortTermTargets.map((target, idx) => (
                    <div key={idx} className="pentest-target">
                      <span className="target-name">{target.target}</span>
                      <span className="target-type">{target.testType}</span>
                      <p className="target-rationale">{target.rationale}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Compliance Mapping */}
      <section className="report-section">
        <button
          className="section-header"
          onClick={() => toggleSection('compliance')}
        >
          <h2 className="section-title">Compliance Mapping</h2>
          <span className={`toggle-icon ${expandedSections.compliance ? 'open' : ''}`}>
            &#9660;
          </span>
        </button>
        {expandedSections.compliance && aiAnalysis?.complianceMapping && (
          <div className="section-content">
            <div className="compliance-grid">
              {/* OWASP Top 10 */}
              {aiAnalysis.complianceMapping.owaspTop10 && (
                <div className="compliance-card">
                  <h3 className="compliance-title">OWASP Top 10</h3>
                  <TrendIndicator direction={aiAnalysis.complianceMapping.owaspTop10.trend} />
                  {aiAnalysis.complianceMapping.owaspTop10.gaps?.length > 0 && (
                    <div className="compliance-gaps">
                      <span className="gaps-label">Gaps:</span>
                      {aiAnalysis.complianceMapping.owaspTop10.gaps.map((gap, idx) => (
                        <span key={idx} className="gap-item">{gap}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* CIS Controls */}
              {aiAnalysis.complianceMapping.cisControls && (
                <div className="compliance-card">
                  <h3 className="compliance-title">CIS Controls</h3>
                  <TrendIndicator direction={aiAnalysis.complianceMapping.cisControls.trend} />
                  {aiAnalysis.complianceMapping.cisControls.gaps?.length > 0 && (
                    <div className="compliance-gaps">
                      <span className="gaps-label">Gaps:</span>
                      {aiAnalysis.complianceMapping.cisControls.gaps.map((gap, idx) => (
                        <span key={idx} className="gap-item">{gap}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* NIST Framework */}
              {aiAnalysis.complianceMapping.nistFramework && (
                <div className="compliance-card nist">
                  <h3 className="compliance-title">NIST Framework</h3>
                  <TrendIndicator direction={aiAnalysis.complianceMapping.nistFramework.overallTrend} />
                  <div className="nist-scores">
                    <div className="nist-score">
                      <span className="score-value">{aiAnalysis.complianceMapping.nistFramework.identifyScore || 0}%</span>
                      <span className="score-label">Identify</span>
                    </div>
                    <div className="nist-score">
                      <span className="score-value">{aiAnalysis.complianceMapping.nistFramework.protectScore || 0}%</span>
                      <span className="score-label">Protect</span>
                    </div>
                    <div className="nist-score">
                      <span className="score-value">{aiAnalysis.complianceMapping.nistFramework.detectScore || 0}%</span>
                      <span className="score-label">Detect</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Recommendation Tracking */}
      <section className="report-section">
        <button
          className="section-header"
          onClick={() => toggleSection('tracking')}
        >
          <h2 className="section-title">Recommendation Tracking</h2>
          <span className={`toggle-icon ${expandedSections.tracking ? 'open' : ''}`}>
            &#9660;
          </span>
        </button>
        {expandedSections.tracking && aiAnalysis?.recommendationTracking && (
          <div className="section-content">
            {/* Addressed from last week */}
            {aiAnalysis.recommendationTracking.addressedFromLastWeek?.length > 0 && (
              <div className="tracking-group">
                <h3 className="group-title resolved">
                  &#10003; Addressed Since Last Week
                </h3>
                <div className="tracking-items">
                  {aiAnalysis.recommendationTracking.addressedFromLastWeek.map((item, idx) => (
                    <div key={idx} className="tracking-item resolved">
                      <div className="item-rec-row">
                        <span className="resolved-icon">&#10003;</span>
                        <span className="item-rec resolved-text">{item.originalRecommendation}</span>
                      </div>
                      <span className="item-status resolved-status">{item.status}</span>
                      {item.evidence && (
                        <p className="item-evidence">
                          {item.autoResolved ? 'Auto-verified: ' : ''}{item.evidence}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Still pending */}
            {aiAnalysis.recommendationTracking.stillPendingFromLastWeek?.length > 0 && (
              <div className="tracking-group">
                <h3 className="group-title pending">
                  &#9888; Still Pending
                </h3>
                <div className="tracking-items">
                  {aiAnalysis.recommendationTracking.stillPendingFromLastWeek.map((item, idx) => {
                    const daysOpen = item.daysOpen || 7;
                    const escalationLevel = item.escalationLevel || (daysOpen > 30 ? 'critical' : daysOpen > 14 ? 'warning' : 'none');
                    return (
                      <div key={idx} className={`tracking-item pending ${escalationLevel !== 'none' ? 'escalate' : ''} ${escalationLevel === 'critical' ? 'escalate-critical' : ''}`}>
                        <div className="item-rec-row">
                          {escalationLevel === 'critical' ? (
                            <span className="escalation-icon critical-pulse">&#9888;</span>
                          ) : escalationLevel === 'warning' ? (
                            <span className="escalation-icon warning">&#9888;</span>
                          ) : (
                            <span className="escalation-icon clock">&#128339;</span>
                          )}
                          <span className="item-rec">{item.originalRecommendation}</span>
                        </div>
                        <div className="item-meta">
                          <span className="item-days">{daysOpen} days open</span>
                          {escalationLevel === 'critical' && (
                            <span className="escalation-badge critical">Critical &mdash; unresolved {daysOpen}+ days</span>
                          )}
                          {escalationLevel === 'warning' && (
                            <span className="escalation-badge warning">Overdue &mdash; needs attention</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* New this week */}
            {aiAnalysis.recommendationTracking.newThisWeek?.length > 0 && (
              <div className="tracking-group">
                <h3 className="group-title new">New This Week</h3>
                <ul className="new-recs-list">
                  {aiAnalysis.recommendationTracking.newThisWeek.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Drill-Down Details */}
      <section className="report-section">
        <button
          className="section-header"
          onClick={() => toggleSection('drilldown')}
        >
          <h2 className="section-title">Drill-Down Details</h2>
          <span className={`toggle-icon ${expandedSections.drilldown ? 'open' : ''}`}>
            &#9660;
          </span>
        </button>
        {expandedSections.drilldown && aiAnalysis?.drillDownDetails && (
          <div className="section-content">
            {/* Critical Findings */}
            {aiAnalysis.drillDownDetails.criticalFindings?.length > 0 && (
              <div className="drilldown-group">
                <h3 className="group-title">Critical Findings</h3>
                <div className="findings-list">
                  {aiAnalysis.drillDownDetails.criticalFindings.map((finding, idx) => (
                    <div key={idx} className="finding-card">
                      <div className="finding-header">
                        <span className="finding-name">{finding.name}</span>
                        <span className="finding-location">{finding.location}</span>
                      </div>
                      <p className="finding-desc">{finding.description}</p>
                      {finding.remediation && (
                        <div className="finding-remediation">
                          <strong>Fix:</strong> {finding.remediation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Infrastructure Changes */}
            {aiAnalysis.drillDownDetails.infrastructureChanges && (
              <div className="drilldown-group">
                <h3 className="group-title">Infrastructure Changes</h3>
                <div className="infra-changes">
                  {aiAnalysis.drillDownDetails.infrastructureChanges.newSubdomains?.length > 0 && (
                    <div className="change-item">
                      <span className="change-label">New Subdomains:</span>
                      <span className="change-value">
                        {aiAnalysis.drillDownDetails.infrastructureChanges.newSubdomains.join(', ')}
                      </span>
                    </div>
                  )}
                  {aiAnalysis.drillDownDetails.infrastructureChanges.newPorts?.length > 0 && (
                    <div className="change-item">
                      <span className="change-label">New Ports:</span>
                      <span className="change-value">
                        {aiAnalysis.drillDownDetails.infrastructureChanges.newPorts.join(', ')}
                      </span>
                    </div>
                  )}
                  {aiAnalysis.drillDownDetails.infrastructureChanges.closedPorts?.length > 0 && (
                    <div className="change-item">
                      <span className="change-label">Closed Ports:</span>
                      <span className="change-value">
                        {aiAnalysis.drillDownDetails.infrastructureChanges.closedPorts.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Blind Spots */}
            {aiAnalysis.trendAnalysis?.blindSpots?.length > 0 && (
              <div className="drilldown-group">
                <h3 className="group-title warning">Scan Blind Spots</h3>
                <p className="blindspots-note">
                  These template categories were not run in this scan period:
                </p>
                <ul className="blindspots-list">
                  {aiAnalysis.trendAnalysis.blindSpots.map((spot, idx) => (
                    <li key={idx}>{spot}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Footer */}
      <div className="report-footer">
        <div className="footer-info">
          <span>Model: {report.model || 'Claude Sonnet 4'}</span>
          <span>Tokens: {report.tokensUsed || 'N/A'}</span>
          <span>Confidence: {aiAnalysis?.trendAnalysis?.confidenceLevel || 'N/A'}%</span>
        </div>
        <div className="footer-actions">
          <button className="nav-button" onClick={() => navigate('/reports')}>
            Back to Reports
          </button>
        </div>
      </div>
    </div>
  );
}

export default WeeklyReportDetail;
